import { Component } from '@theme/component';

/**
 * FIND 10 THINGS: hidden-object game for sections/odessey-find-10-things.liquid.
 *
 * Coordinates: every target is stored as a percent of the artwork's width/height, per
 * composition ("d" desktop 16:9, "m" mobile 4:5). Hit-testing, found markers and the
 * magnifier all read the same rendered image box, so they stay aligned at any size.
 * When the section has a mobile artwork and "Mobile object" blocks, phones use that separate
 * map instead: its own ten objects, measured on the portrait image as normalized 0 to 1
 * rectangles (centre x/y, width/height). Each gets a transparent button of the same size,
 * at least MIN_TARGET px, for keyboard and screen-reader users; taps use the same rectangles.
 *
 * Rewards: this file never creates discount codes. On completion it posts the finds to the
 * merchant's reward endpoint, which validates them server-side and issues a single-use code.
 * localStorage only remembers progress for convenience; it is not used to grant anything.
 */

const STORAGE_KEY = 'odessey-f10:v1';
const MOBILE_QUERY = '(max-width: 749px)';
const CODE_PATTERN = /^[A-Z0-9-]{4,40}$/;
const TAP_SLOP = 10;
// Smallest tap area for a mobile-map object, in CSS px; small objects get a larger invisible area.
const MIN_TARGET = 48;
const DEBUG_PARAM = 'debugTargets';
const KEY_STEP = 2;
const KEY_STEP_LARGE = 8;
// Set when a visitor submits the store's early-access form from the game.
const REGISTRATION_KEY = 'odessey-f10-registering';
// The early-access block's own "show thank-you after reload" marker (blocks/early-access.liquid).
const EARLY_ACCESS_PENDING_KEY = 'odessey-early-access-pending';
const REGISTRATION_TTL = 15 * 60 * 1000;
const REGISTRATION_TAG = 'find-10-things';

/**
 * @typedef {{ x: number, y: number, r: number }} Spot
 * @typedef {{ key: string, bonus: boolean, label: string, message: string, hint?: string, d: Spot, m: Spot }} Target
 * A target on the current composition. Percent of the artwork; r for a circle, w/h for a rectangle.
 * @typedef {{ key: string, bonus: boolean, label: string, message: string, hint: string, x: number, y: number, r?: number, w?: number, h?: number }} Active
 * @typedef {{ cx: number, cy: number, hw: number, hh: number, round: boolean }} HitBox
 * @typedef {{ key: string, x: number, y: number, t: number, c: 'd' | 'm' }} Find
 * @typedef {{ x: number, y: number }} Point
 *
 * @typedef {object} Refs
 * @property {HTMLElement} stage
 * @property {HTMLElement} scene
 * @property {HTMLImageElement} image
 * @property {HTMLElement} marks
 * @property {HTMLElement} ripple
 * @property {HTMLElement} lens
 * @property {HTMLElement} cover
 * @property {HTMLButtonElement} startButton
 * @property {HTMLElement} status
 * @property {HTMLElement} count
 * @property {HTMLButtonElement} lensToggle
 * @property {HTMLElement} lensLabel
 * @property {HTMLElement[]} [items]
 * @property {HTMLElement} bonus
 * @property {HTMLElement} complete
 * @property {HTMLElement} reward
 * @property {HTMLOutputElement} code
 * @property {HTMLButtonElement} copyButton
 * @property {HTMLElement} expiry
 * @property {HTMLElement} errorText
 * @property {HTMLTemplateElement} strings
 * @property {HTMLScriptElement} config
 * @property {HTMLElement} [countdown]
 * @property {HTMLAnchorElement} [applyLink]
 * @property {HTMLElement} [applyStatus]
 * @property {HTMLElement} [register]
 * @property {HTMLElement} [registerError]
 * @property {HTMLElement} [registerUnavailable]
 * @property {HTMLButtonElement} [registerButton]
 * @property {HTMLElement} [targets]
 * @property {HTMLElement} [total]
 *
 * @extends {Component<Refs>}
 */
class OdesseyFindGame extends Component {
  requiredRefs = ['scene', 'image', 'lens', 'marks', 'count', 'status', 'config', 'complete', 'reward', 'stage'];

  /** @type {Target[]} */
  #targets = [];
  /** Separate map for the mobile artwork, in percent. Empty when the section has none. @type {Active[]} */
  #mobileTargets = [];
  /** @type {Set<string>} */
  #found = new Set();
  /** @type {Find[]} */
  #finds = [];
  #bonusFound = false;
  #startedAt = 0;
  /** @type {string | null} */
  #token = null;
  /** @type {Promise<string | null> | null} */
  #sessionRequest = null;
  /** @type {{ code: string, expiresAt?: string } | null} */
  #claim = null;
  #claiming = false;
  #lensOn = false;
  /** @type {Point} */
  #point = { x: 50, y: 50 };
  /** @type {{ id: number, x: number, y: number, type: string, moved: boolean } | null} */
  #press = null;
  /** @type {Set<string>} */
  #tracked = new Set();
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {IntersectionObserver | null} */
  #viewObserver = null;
  /** @type {MediaQueryList | null} */
  #mobileQuery = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #toastTimer;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #countdownTimer;
  #eaJoined = false;
  /** The winner closed or skipped the early-access form; registration is optional. */
  #eaSkipped = false;
  /** Server time minus this device's clock, from Shopify's Date header. */
  #clockOffset = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #autoOpenTimer;
  /** @type {AbortController | null} */
  #registration = null;
  #lensSrc = '';
  /** @type {Set<string>} */
  #preloaded = new Set();
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hintTimer;
  #hintIndex = 0;
  #debug = false;
  /** @type {HTMLElement | null} */
  #debugPanel = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#teardown();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  #setup() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const { scene, image } = this.refs;

    try {
      const parsed = JSON.parse(this.refs.config.textContent || '{}');
      this.#targets = Array.isArray(parsed.targets) ? parsed.targets.filter(isTarget) : [];
      this.#mobileTargets =
        this.dataset.mobileMap === 'true' && Array.isArray(parsed.mobile)
          ? parsed.mobile.filter(isMobileTarget).map(toActive)
          : [];
    } catch {
      this.#targets = [];
      this.#mobileTargets = [];
    }
    this.#debug = debugAllowed(this.dataset.debugEditor === 'true');
    this.toggleAttribute('data-debug', this.#debug);

    this.#mobileQuery = window.matchMedia(MOBILE_QUERY);
    this.#mobileQuery.addEventListener(
      'change',
      () => {
        this.#renderProgress();
        this.#renderMarks();
      },
      { signal }
    );
    this.#lensOn = window.matchMedia('(pointer: fine)').matches;

    scene.addEventListener('pointerdown', (event) => this.#onPointerDown(event), { signal });
    scene.addEventListener('pointermove', (event) => this.#onPointerMove(event), { signal });
    scene.addEventListener('pointerup', (event) => this.#onPointerUp(event), { signal });
    scene.addEventListener('pointercancel', () => this.#endPress(), { signal });
    scene.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') this.#hideLens();
    }, { signal });
    scene.addEventListener('keydown', (event) => this.#onKeyDown(event), { signal });
    scene.addEventListener('blur', () => this.#hideLens(), { signal });
    scene.addEventListener('contextmenu', (event) => {
      if (this.dataset.state === 'playing') event.preventDefault();
    }, { signal });
    // Target buttons ignore pointers (taps are hit-tested by position), so clicks here come from
    // the keyboard or a screen reader.
    this.refs.targets?.addEventListener('click', (event) => this.#onTargetActivate(event), { signal });
    image.addEventListener('load', () => this.#renderMarks(), { signal });
    window.addEventListener(
      'resize',
      () => {
        // Mobile browsers fire resize as toolbars move; keep the lens during an active drag.
        if (!this.#press) this.#hideLens();
        this.#renderMarks();
      },
      { signal, passive: true }
    );

    this.#viewObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.#track('game_view', {}, 'game_view');
          this.#viewObserver?.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    this.#viewObserver.observe(this);

    this.#restore();
    this.#refreshClaim();
    this.#startCountdown();
    this.#syncClock();
    this.#syncLensToggle();
    this.#renderAll();
    this.#afterRegistration();
    // A winner who left before the reward service answered asks again (or sees the saved code).
    if (this.dataset.rewardMode === 'endpoint' && this.dataset.state === 'complete') this.#claimReward();
    // A winner from before registration was turned off who never saw the code gets it now.
    if (this.dataset.register === 'off' && this.dataset.state === 'complete' && !this.#eaJoined && !this.#eaSkipped) {
      this.skipRegistration({ quiet: true });
    }
    if (this.#debug) this.#renderDebugPanel();
  }

  /**
   * A finished game always shows the current code. Saved progress can hold a claim that has since
   * expired or a code that has changed; without this, a returning winner saw "You noticed" with no code.
   */
  #refreshClaim() {
    if (this.dataset.rewardMode !== 'registration' || this.dataset.state !== 'complete') return;
    if (!this.#eaJoined && !this.#eaSkipped) return;
    const code = (this.dataset.rewardCode || '').trim().toUpperCase();
    if (!CODE_PATTERN.test(code) || this.#claim?.code === code) return;
    this.#claim = { code, expiresAt: this.dataset.endsAt || undefined };
    this.#save();
  }

  #teardown() {
    this.#abort?.abort();
    this.#abort = null;
    this.#viewObserver?.disconnect();
    this.#viewObserver = null;
    clearTimeout(this.#toastTimer);
    clearInterval(this.#countdownTimer);
    clearTimeout(this.#autoOpenTimer);
    clearTimeout(this.#hintTimer);
    this.#debugPanel?.remove();
    this.#debugPanel = null;
  }

  /* ----------------------------------------------------------------- public handlers */

  start() {
    if (!this.#targets.length || this.#checkDeadline()) return;
    if (!this.#startedAt) this.#startedAt = Date.now();
    this.dataset.state = 'playing';
    this.#track('game_start', { resumed: this.#found.size > 0 });
    this.#save();
    this.#ensureSession();
    this.#preloadLens();
    this.#renderTargets();
    this.#scheduleHint();
    this.refs.scene.focus({ preventScroll: true });
  }

  restart() {
    this.#found.clear();
    this.#finds = [];
    this.#bonusFound = false;
    this.#startedAt = 0;
    this.#token = null;
    this.#sessionRequest = null;
    this.#tracked.delete('game_complete');
    for (const key of [...this.#tracked]) {
      if (key.startsWith('object_found:') || key === 'journal_clue_found') this.#tracked.delete(key);
    }
    this.dataset.state = 'idle';
    this.#hintIndex = 0;
    clearTimeout(this.#hintTimer);
    this.refs.status.textContent = '';
    this.#save();
    this.#renderAll();
    this.refs.stage.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    this.refs.startButton?.focus({ preventScroll: true });
  }

  toggleLens() {
    this.#lensOn = !this.#lensOn;
    this.#syncLensToggle();
    if (this.#lensOn) this.#preloadLens();
    if (!this.#lensOn) this.#hideLens();
  }

  async copyCode() {
    const code = this.#claim?.code;
    if (!code) return;
    let copied = false;
    try {
      await navigator.clipboard.writeText(code);
      copied = true;
    } catch {
      // Clipboard API unavailable: select the code so the visitor can copy it manually.
      const selection = window.getSelection();
      if (selection) {
        const range = document.createRange();
        range.selectNodeContents(this.refs.code);
        selection.removeAllRanges();
        selection.addRange(range);
        copied = document.execCommand?.('copy') ?? false;
      }
    }
    if (copied) {
      const button = this.refs.copyButton;
      button.textContent = this.#string('copied');
      setTimeout(() => {
        button.textContent = this.#string('copy_code');
      }, 2000);
    }
    this.#track('discount_copy', { copied });
  }

  retryClaim() {
    this.#claimReward();
  }

  /**
   * Applies the shared code to this browser's checkout (Shopify's /discount/CODE link sets it)
   * without leaving the page. Falls back to following the link, which lands on the cart.
   * @param {Event} event
   */
  async applyCode(event) {
    const link = this.refs.applyLink;
    if (!link || !this.#claim) return;
    event.preventDefault();
    this.#track('discount_apply', { code_type: 'shared' });
    try {
      const url = new URL(link.href, window.location.href);
      url.searchParams.set('redirect', '/cart.js');
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(String(response.status));
      if (this.refs.applyStatus) this.refs.applyStatus.textContent = this.#string('applied');
    } catch {
      window.location.href = link.href;
    }
  }

  /**
   * Opens the store's existing early-access form (blocks/early-access.liquid) instead of a copy, so
   * sign-ups keep its validation, spam check, tags and any Google Sheet sync. The block is not
   * modified: the game listens for the form's `formdata` event, which only fires when the form
   * really submits (after the block's validation), to add a "find-10-things" tag and take over
   * the confirmation screen.
   */
  openRegistration() {
    clearTimeout(this.#autoOpenTimer);
    if (this.#checkDeadline() || this.dataset.state !== 'complete') return;

    const opener = document.querySelector('[data-early-access-open]');
    const id = opener instanceof HTMLElement ? opener.dataset.earlyAccessOpen : '';
    const dialog = id ? document.getElementById(`EarlyAccessForm-${id}`) : null;
    const form = dialog?.querySelector('form[data-early-access-form]');
    if (!(opener instanceof HTMLElement) || !(form instanceof HTMLFormElement)) {
      if (this.refs.registerUnavailable) this.refs.registerUnavailable.hidden = false;
      return;
    }
    if (this.refs.registerError) this.refs.registerError.hidden = true;

    this.#registration?.abort();
    const controller = new AbortController();
    this.#registration = controller;
    form.addEventListener(
      'formdata',
      (event) => {
        const tags = String(event.formData.get('contact[tags]') || '');
        if (!tags.split(',').map((tag) => tag.trim()).includes(REGISTRATION_TAG)) {
          event.formData.set('contact[tags]', tags ? `${tags},${REGISTRATION_TAG}` : REGISTRATION_TAG);
        }
        try {
          window.sessionStorage.setItem(REGISTRATION_KEY, JSON.stringify({ id: this.id, t: Date.now() }));
          // The game shows the confirmation (with the reward), so the block skips its thank-you.
          window.sessionStorage.removeItem(EARLY_ACCESS_PENDING_KEY);
        } catch {}
        this.#track('registration_submit');
      },
      { signal: controller.signal }
    );
    dialog?.addEventListener(
      'close',
      () => {
        // Closed while submitting (Shopify's spam check closes it): keep waiting for the sign-up.
        if (form.getAttribute('aria-busy') === 'true') return;
        controller.abort();
        // Closed without registering (close button, Esc, backdrop or skip): still show the code.
        this.skipRegistration();
      },
      { once: true, signal: controller.signal }
    );

    const skipLabel = this.dataset.skipLabel || '';
    if (dialog && skipLabel && !this.#eaJoined) {
      const skip = document.createElement('button');
      skip.type = 'button';
      skip.className = 'early-access-modal__skip';
      skip.textContent = skipLabel;
      skip.addEventListener('click', () => dialog.close(), { signal: controller.signal });
      form.after(skip);
      controller.signal.addEventListener('abort', () => skip.remove(), { once: true });
    }

    this.#track('registration_open');
    opener.click();
  }

  /**
   * Registration is optional. A winner who skips, or closes the early-access form, still gets the
   * code; winners who register keep the confirmed flow in #afterRegistration.
   */
  skipRegistration({ quiet = false } = {}) {
    clearTimeout(this.#autoOpenTimer);
    if (this.dataset.rewardMode !== 'registration' || this.#eaJoined) return;
    if (this.#checkDeadline() || this.dataset.state !== 'complete') return;
    const code = (this.dataset.rewardCode || '').trim().toUpperCase();
    if (!CODE_PATTERN.test(code)) {
      if (this.refs.registerUnavailable) this.refs.registerUnavailable.hidden = false;
      return;
    }

    // Skipping from the game panel while the form is open closes it too.
    this.#registration?.abort();
    this.#registration = null;
    for (const dialog of document.querySelectorAll('dialog.early-access-modal[open]')) {
      if (dialog instanceof HTMLDialogElement) dialog.close();
    }

    const first = !this.#eaSkipped;
    this.#eaSkipped = true;
    this.#claim = { code, expiresAt: this.dataset.endsAt || undefined };
    this.#save();
    if (first) {
      this.#track('registration_skipped');
      this.#track('reward_claimed', { code_type: 'shared', registered: false }, 'reward_claimed');
    }
    this.#renderAll();
    if (quiet) return;
    this.refs.complete.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    this.refs.copyButton?.focus({ preventScroll: true });
  }

  trackShopClick() {
    this.#track('shop_the_drop_click', { from: 'game_complete' });
  }

  /* ----------------------------------------------------------------- input */

  /** @param {PointerEvent} event */
  #onPointerDown(event) {
    if (this.dataset.state !== 'playing' || !event.isPrimary) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.dataset.input = event.pointerType === 'mouse' ? 'mouse' : 'touch';
    this.#press = { id: event.pointerId, x: event.clientX, y: event.clientY, type: event.pointerType, moved: false };

    if (event.pointerType !== 'mouse' && this.#lensOn) {
      event.preventDefault();
      try {
        this.refs.scene.setPointerCapture(event.pointerId);
      } catch {
        // The pointer may already be gone (e.g. a cancelled touch); dragging still works without capture.
      }
      this.#point = this.#inspectPoint(event);
      this.#showLens();
    }
  }

  /** @param {PointerEvent} event */
  #onPointerMove(event) {
    if (this.dataset.state !== 'playing' || !event.isPrimary) return;

    if (event.pointerType === 'mouse') {
      this.dataset.input = 'mouse';
      this.#point = this.#inspectPoint(event);
      if (this.#lensOn) this.#showLens();
      return;
    }

    const press = this.#press;
    if (!press || press.id !== event.pointerId) return;
    if (Math.hypot(event.clientX - press.x, event.clientY - press.y) > TAP_SLOP) press.moved = true;
    if (this.#lensOn) {
      this.#point = this.#inspectPoint(event);
      this.#showLens();
    }
  }

  /** @param {PointerEvent} event */
  #onPointerUp(event) {
    const press = this.#press;
    if (this.dataset.state !== 'playing' || !press || press.id !== event.pointerId) return;
    this.#press = null;

    if (event.pointerType === 'mouse') {
      if (!press.moved) this.#look(this.#inspectPoint(event));
      return;
    }

    if (this.#lensOn) {
      // Touch magnifier: drag the lens over something, lift to look closer.
      this.#look(this.#inspectPoint(event));
      this.#hideLens();
    } else if (!press.moved) {
      this.#look(this.#inspectPoint(event));
    }
  }

  #endPress() {
    this.#press = null;
    if (this.dataset.input === 'touch') this.#hideLens();
  }

  /** @param {KeyboardEvent} event */
  #onKeyDown(event) {
    if (this.dataset.state !== 'playing') return;
    const onTarget = event.target instanceof Element && event.target.closest('.f10__target');
    // A focused target button activates itself (its click handler); don't also look at the lens point.
    if (onTarget && (event.key === 'Enter' || event.key === ' ')) return;
    const step = event.shiftKey ? KEY_STEP_LARGE : KEY_STEP;
    const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    const move = moves[/** @type {keyof typeof moves} */ (event.key)];

    if (move) {
      event.preventDefault();
      this.dataset.input = 'keyboard';
      const box = this.#box();
      const aspect = box.width / Math.max(box.height, 1);
      this.#point = {
        x: clamp(this.#point.x + move[0], 0, 100),
        y: clamp(this.#point.y + move[1] * aspect, 0, 100),
      };
      this.#showLens(true);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#look(this.#point);
    } else if (event.key === 'Escape') {
      this.#hideLens();
    }
  }

  /**
   * The point being inspected, as percent of the image. With the touch magnifier on, it sits
   * above the finger so the finger never covers what the lens shows.
   * @param {PointerEvent} event
   * @returns {Point}
   */
  #inspectPoint(event) {
    const box = this.#box();
    let y = event.clientY;
    if (event.pointerType !== 'mouse' && this.#lensOn) y -= this.#lensSize() * 0.75;
    return {
      x: clamp(((event.clientX - box.left) / box.width) * 100, 0, 100),
      y: clamp(((y - box.top) / box.height) * 100, 0, 100),
    };
  }

  /* ----------------------------------------------------------------- gameplay */

  /** @param {Point} point */
  #look(point) {
    const box = this.#box();
    const comp = this.#composition();
    const px = (point.x / 100) * box.width;
    const py = (point.y / 100) * box.height;
    /** @type {Active | null} */
    let best = null;
    let bestDistance = Infinity;
    let hitFound = false;

    for (const target of this.#active()) {
      const hit = this.#hitBox(target, box);
      const dx = px - hit.cx;
      const dy = py - hit.cy;
      // Circles compare pixel distance (as before); rectangles compare how far inside, 0 centre to 1 edge,
      // so where two padded areas overlap the tap goes to the object whose centre is closer.
      const distance = hit.round ? Math.hypot(dx, dy) : Math.max(Math.abs(dx) / hit.hw, Math.abs(dy) / hit.hh);
      if (distance > (hit.round ? hit.hw : 1)) continue;

      const done = target.bonus ? this.#bonusFound : this.#found.has(target.key);
      if (done) {
        hitFound = true;
        continue;
      }
      if (distance < bestDistance) {
        best = target;
        bestDistance = distance;
      }
    }

    if (this.#debug) this.#debugTap(point, best);

    if (best) {
      this.#discover(best, point, comp);
    } else {
      this.#rippleAt(point);
      this.#toast(this.#string(hitFound ? 'already_found' : 'not_here'), 1600);
    }
  }

  /** @param {MouseEvent} event */
  #onTargetActivate(event) {
    const button = event.target instanceof Element ? event.target.closest('.f10__target') : null;
    if (!(button instanceof HTMLElement) || this.dataset.state !== 'playing') return;
    const target = this.#mobileTargets.find((item) => item.key === button.dataset.key);
    if (!target || this.#composition() !== 'm') return;
    if (this.#found.has(target.key)) {
      this.#toast(this.#string('already_found'), 1600);
      return;
    }
    this.dataset.input = 'keyboard';
    this.#discover(target, { x: target.x, y: target.y }, 'm');
  }

  /**
   * @param {Active} target
   * @param {Point} point
   * @param {'d' | 'm'} comp
   */
  #discover(target, point, comp) {
    if (target.bonus) {
      this.#bonusFound = true;
      this.#track('journal_clue_found', {}, 'journal_clue_found');
      this.#toast(`<strong>${escapeHtml(this.#string('bonus_found'))}</strong>${escapeHtml(target.message)}`, 4200, true);
      this.#save();
      this.#renderMarks();
      this.#renderBonus();
      return;
    }

    if (this.#found.has(target.key)) return;
    this.#found.add(target.key);
    this.#finds.push({
      key: target.key,
      x: round2(point.x),
      y: round2(point.y),
      t: Math.max(0, Date.now() - this.#startedAt),
      c: comp,
    });

    const total = this.#total();
    const count = this.#foundCount();
    this.#track('object_found', { object: target.key, found_count: count, total }, `object_found:${target.key}`);
    this.#toast(
      `<strong>${escapeHtml(this.#string('found_one'))} ${count} / ${total} · ${escapeHtml(target.label)}</strong>${escapeHtml(target.message)}`,
      3200,
      true
    );
    this.#save();
    this.#renderMarks();
    this.#renderProgress();

    if (count >= total) this.#completeGame();
    else this.#scheduleHint();
  }

  #completeGame() {
    this.#hideLens();
    clearTimeout(this.#hintTimer);
    this.dataset.state = 'complete';
    this.#track('game_complete', { duration_ms: Date.now() - this.#startedAt, bonus_found: this.#bonusFound }, 'game_complete');
    this.#save();
    this.#renderAll();
    this.refs.complete.focus({ preventScroll: true });
    this.refs.complete.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    this.#claimReward();
    if (this.dataset.rewardMode === 'registration' && !this.#eaJoined && !this.#eaSkipped) {
      // With registration turned off (no register label), winners get the code straight away.
      if (this.dataset.register === 'off') this.skipRegistration();
      else this.#autoOpenTimer = setTimeout(() => this.openRegistration(), 1200);
    }
  }

  /**
   * The targets for the artwork on screen: the separate mobile map on phones when there is one,
   * otherwise each block's desktop or mobile spot.
   * @returns {Active[]}
   */
  #active() {
    const comp = this.#composition();
    if (comp === 'm' && this.#mobileTargets.length) return this.#mobileTargets;
    return this.#targets.map((target) => ({
      key: target.key,
      bonus: target.bonus,
      label: target.label,
      message: target.message,
      hint: target.hint || '',
      ...target[comp],
    }));
  }

  /**
   * A target's tap area in px from the image's top-left corner. hw/hh are half the width/height
   * (the radius for circles). Rectangles never shrink below MIN_TARGET.
   * @param {Active} target
   * @param {DOMRect} box
   * @returns {HitBox}
   */
  #hitBox(target, box) {
    const cx = (target.x / 100) * box.width;
    const cy = (target.y / 100) * box.height;
    if (target.w !== undefined && target.h !== undefined) {
      return {
        cx,
        cy,
        hw: Math.max((target.w / 100) * box.width, MIN_TARGET) / 2,
        hh: Math.max((target.h / 100) * box.height, MIN_TARGET) / 2,
        round: false,
      };
    }
    const r = Math.max(((target.r ?? 2) / 100) * box.width, this.dataset.input === 'touch' ? 22 : 14);
    return { cx, cy, hw: r, hh: r, round: true };
  }

  #total() {
    return this.#active().filter((target) => !target.bonus).length;
  }

  /** Objects found on the artwork on screen. Shared keys (kite, dog...) count on both. */
  #foundCount() {
    return this.#active().filter((target) => !target.bonus && this.#found.has(target.key)).length;
  }

  /** @returns {'d' | 'm'} */
  #composition() {
    const mobile = this.#mobileQuery?.matches ?? false;
    return mobile && this.dataset.hasMobileArt === 'true' ? 'm' : 'd';
  }

  /* ----------------------------------------------------------------- rewards */

  #endpoint() {
    const raw = (this.dataset.rewardEndpoint || '').trim();
    if (!raw) return '';
    try {
      const url = new URL(raw, window.location.origin);
      if (url.protocol !== 'https:' && url.hostname !== 'localhost') return '';
      return url.href.replace(/\/+$/, '');
    } catch {
      return '';
    }
  }

  /** Requests a signed game session so the server can check elapsed time. Never blocks play. */
  #ensureSession() {
    const endpoint = this.#endpoint();
    if (!endpoint || this.#token) return Promise.resolve(this.#token);
    if (this.#sessionRequest) return this.#sessionRequest;

    this.#sessionRequest = fetch(`${endpoint}/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
      credentials: 'omit',
      signal: this.#abort?.signal,
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (data && typeof data.token === 'string') {
          this.#token = data.token;
          this.#save();
        }
        return this.#token;
      })
      .catch(() => null)
      .finally(() => {
        this.#sessionRequest = null;
      });

    return this.#sessionRequest;
  }

  async #claimReward() {
    if (this.dataset.state !== 'complete' || this.#claiming) return;

    // Campaign mode: the code is revealed only after the server confirms the early-access sign-up.
    if (this.dataset.rewardMode === 'registration') {
      this.#renderAll();
      return;
    }

    if (this.#claim && !isExpired(this.#claim.expiresAt)) {
      this.#renderReward('success');
      return;
    }

    const endpoint = this.#endpoint();
    if (!endpoint) {
      this.#renderReward('unavailable');
      return;
    }

    this.#claiming = true;
    this.#renderReward('loading');

    try {
      const token = await this.#ensureSession();
      if (!token) throw new ClaimError('reward_error');

      const response = await fetch(`${endpoint}/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        signal: this.#abort?.signal,
        body: JSON.stringify({ token, finds: this.#finds, bonus: this.#bonusFound }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok && typeof data.code === 'string' && CODE_PATTERN.test(data.code)) {
        this.#claim = { code: data.code, expiresAt: typeof data.expiresAt === 'string' ? data.expiresAt : undefined };
        this.#save();
        this.#track('reward_claimed', { reused: Boolean(data.reused) }, 'reward_claimed');
        this.#renderReward('success');
        return;
      }

      // No capped discount is running right now (e.g. between two of them): say so instead of failing.
      if (data.error === 'unavailable') {
        this.#renderReward('unavailable');
        return;
      }
      if (response.status === 429 || data.error === 'rate_limited') throw new ClaimError('error_rate_limited');
      if (data.error === 'invalid' || data.error === 'expired_session') {
        // The session can't be used again; a fresh one is needed for the next attempt.
        this.#token = null;
        this.#save();
        throw new ClaimError('error_invalid');
      }
      throw new ClaimError('reward_error');
    } catch (error) {
      if (this.#abort?.signal.aborted) return;
      const key = error instanceof ClaimError ? error.key : 'reward_error';
      this.refs.errorText.textContent = this.#string(key);
      this.#renderReward('error');
    } finally {
      this.#claiming = false;
    }
  }

  /* ----------------------------------------------------------------- rendering */

  #renderAll() {
    const state = this.dataset.state;
    const registration = this.dataset.rewardMode === 'registration';
    const rewarded = Boolean(this.#claim) && (!registration || this.#eaJoined || this.#eaSkipped);
    if (this.refs.register) this.refs.register.hidden = rewarded;
    if (registration) this.refs.reward.dataset.rewardVia = this.#eaJoined ? 'registered' : 'skipped';
    this.refs.stage.hidden = state === 'complete';
    this.refs.complete.hidden = state !== 'complete';
    if (this.refs.startButton) {
      const label = this.refs.startButton.dataset.label || this.refs.startButton.textContent?.trim() || '';
      this.refs.startButton.dataset.label = label;
      this.refs.startButton.textContent = this.#found.size > 0 ? this.#string('keep_looking') : label;
    }
    this.#renderProgress();
    this.#renderMarks();
    this.#renderBonus();
    if (state === 'complete') this.#renderReward(rewarded ? 'success' : 'idle');
  }

  #renderProgress() {
    this.refs.count.textContent = String(this.#foundCount());
    if (this.refs.total) this.refs.total.textContent = String(this.#total());
    for (const item of this.refs.items ?? []) {
      const found = this.#found.has(item.dataset.key || '');
      item.toggleAttribute('data-found', found);
      const state = item.querySelector('[data-item-state]');
      if (state) state.textContent = this.#string(found ? 'found_state' : 'not_found_yet');
    }
  }

  #renderMarks() {
    const box = this.#box();
    const fragment = document.createDocumentFragment();
    for (const spot of this.#active()) {
      const done = spot.bonus ? this.#bonusFound : this.#found.has(spot.key);
      if (!done) continue;
      const mark = document.createElement('span');
      mark.className = `f10__mark${spot.bonus ? ' f10__mark--bonus' : ''}`;
      mark.style.setProperty('--x', `${spot.x}%`);
      mark.style.setProperty('--y', `${spot.y}%`);
      if (spot.w !== undefined && spot.h !== undefined) {
        // Rectangle targets: an oval around the object itself, not its padded tap area.
        mark.style.setProperty('--w', `${Math.max((spot.w / 100) * box.width * 1.15, 28)}px`);
        mark.style.setProperty('--h', `${Math.max((spot.h / 100) * box.height * 1.15, 28)}px`);
      } else {
        mark.style.setProperty('--d', `${Math.max(((spot.r ?? 2) / 100) * box.width * 1.5, 28)}px`);
      }
      fragment.append(mark);
    }
    this.refs.marks.replaceChildren(fragment);
    this.#renderTargets();
  }

  /**
   * One transparent button per mobile-map object, sized and placed exactly like its tap area, so
   * keyboard and screen-reader users can find objects too. Invisible (and ignored by pointers)
   * unless debug mode outlines them. In debug mode the desktop circles are outlined as well.
   */
  #renderTargets() {
    const layer = this.refs.targets;
    if (!layer) return;
    const mobileMap = this.#composition() === 'm' && this.#mobileTargets.length > 0;
    layer.inert = this.dataset.state !== 'playing';
    if (!mobileMap && !this.#debug) {
      layer.replaceChildren();
      return;
    }
    const box = this.#box();
    if (!box.width || !box.height) return;
    const found = this.#string('found_state');
    const tagName = mobileMap ? 'BUTTON' : 'SPAN';
    // Update existing nodes in place so a focused button keeps focus after a find or a resize.
    const existing = new Map([...layer.children].map((node) => [/** @type {HTMLElement} */ (node).dataset.key, node]));
    /** @type {Set<Element>} */
    const keep = new Set();
    this.#active().forEach((target, index) => {
      const hit = this.#hitBox(target, box);
      const done = target.bonus ? this.#bonusFound : this.#found.has(target.key);
      let node = /** @type {HTMLElement | undefined} */ (existing.get(target.key));
      if (!node || node.tagName !== tagName) {
        node = document.createElement(tagName.toLowerCase());
        node.dataset.key = target.key;
        layer.append(node);
      }
      keep.add(node);
      node.className = `f10__target${hit.round ? ' f10__target--round' : ''}`;
      node.style.left = `${((hit.cx - hit.hw) / box.width) * 100}%`;
      node.style.top = `${((hit.cy - hit.hh) / box.height) * 100}%`;
      node.style.width = `${((hit.hw * 2) / box.width) * 100}%`;
      node.style.height = `${((hit.hh * 2) / box.height) * 100}%`;
      if (node instanceof HTMLButtonElement) {
        node.type = 'button';
        node.setAttribute('aria-label', done ? `${target.label}, ${found}` : target.label);
        node.setAttribute('aria-disabled', String(done));
      } else {
        node.setAttribute('aria-hidden', 'true');
      }
      node.toggleAttribute('data-found', done);
      if (this.#debug) {
        // Keep labels inside the scene: right-hand areas label to the left, top ones below.
        node.toggleAttribute('data-tag-end', hit.cx > box.width / 2);
        node.toggleAttribute('data-tag-below', hit.cy - hit.hh < 32);
        const tag = document.createElement('span');
        tag.className = 'f10__target-tag';
        tag.setAttribute('aria-hidden', 'true');
        tag.textContent = `${index + 1} ${target.label}`;
        const coords = document.createElement('span');
        coords.textContent = describeSpot(target);
        tag.append(coords);
        node.replaceChildren(tag);
      }
    });
    for (const node of [...layer.children]) {
      if (!keep.has(node)) node.remove();
    }
  }

  /* ----------------------------------------------------------------- hints */

  /** After a while without a find, show a gentle hint for one object that has hint text. */
  #scheduleHint() {
    clearTimeout(this.#hintTimer);
    const delay = Number(this.dataset.hintDelay) * 1000;
    if (!delay || this.dataset.state !== 'playing') return;
    this.#hintTimer = setTimeout(() => this.#showHint(), delay);
  }

  #showHint() {
    if (this.dataset.state !== 'playing') return;
    const pending = this.#active().filter((target) => !target.bonus && target.hint && !this.#found.has(target.key));
    if (!pending.length) return;
    // Don't spend a hint on a hidden tab or while the game is scrolled away.
    const box = this.#box();
    if (document.hidden || box.bottom < 0 || box.top > window.innerHeight) {
      this.#scheduleHint();
      return;
    }
    const target = pending[this.#hintIndex % pending.length];
    this.#hintIndex += 1;
    this.#toast(`<strong>${escapeHtml(this.#string('hint'))}</strong>${escapeHtml(target.hint)}`, 6000, true);
    this.#track('hint_shown', { object: target.key });
    this.#scheduleHint();
  }

  /* ----------------------------------------------------------------- debug (preview only) */

  /** A readout under the scene: last tap in normalized coordinates and the active map as JSON. */
  #renderDebugPanel() {
    const panel = document.createElement('div');
    panel.className = 'f10__debug';
    const heading = document.createElement('p');
    heading.textContent = 'Target debug. Tap the artwork to read its coordinates (0 to 1).';
    const tap = document.createElement('p');
    tap.dataset.debugTap = '';
    tap.textContent = 'Last tap: none yet';
    const copy = document.createElement('button');
    copy.type = 'button';
    copy.className = 'button-secondary';
    copy.textContent = 'Copy target map';
    copy.addEventListener('click', async () => {
      const map = this.#active().map((target) => ({ id: target.key, name: target.label, ...normalizedSpot(target) }));
      try {
        await navigator.clipboard.writeText(JSON.stringify(map, null, 2));
        copy.textContent = 'Copied';
      } catch {
        console.info('[Find 10 Things] target map', map);
        copy.textContent = 'Logged to console';
      }
    });
    panel.append(heading, tap, copy);
    this.refs.scene.after(panel);
    this.#debugPanel = panel;
  }

  /**
   * @param {Point} point
   * @param {Active | null} hit
   */
  #debugTap(point, hit) {
    const readout = this.#debugPanel?.querySelector('[data-debug-tap]');
    if (!readout) return;
    const where = `x ${(point.x / 100).toFixed(3)}, y ${(point.y / 100).toFixed(3)}`;
    readout.textContent = `Last tap: ${where} (${this.#composition() === 'm' ? 'mobile' : 'desktop'} artwork)${hit ? `, hit ${hit.key}` : ''}`;
  }

  #renderBonus() {
    if (this.refs.bonus) this.refs.bonus.hidden = !this.#bonusFound;
  }

  /** @param {'idle' | 'loading' | 'success' | 'error' | 'unavailable'} state */
  #renderReward(state) {
    this.refs.reward.dataset.rewardState = state;
    if (state !== 'success' || !this.#claim) {
      this.refs.code.textContent = '';
      for (const inline of this.querySelectorAll('.f10__code-inline')) inline.textContent = '';
      return;
    }
    this.refs.code.textContent = this.#claim.code;
    const link = this.refs.applyLink;
    if (link) link.href = `${link.dataset.discountBase}${encodeURIComponent(this.#claim.code)}?redirect=${encodeURIComponent(link.dataset.cartUrl || '/cart')}`;
    if (this.dataset.rewardMode === 'registration') {
      for (const inline of this.querySelectorAll('.f10__code-inline')) inline.textContent = this.#claim.code;
      // The terms line already states the end date for the campaign code.
      this.refs.expiry.textContent = '';
      return;
    }
    const expires = this.#claim.expiresAt ? new Date(this.#claim.expiresAt) : null;
    this.refs.expiry.textContent =
      expires && !Number.isNaN(expires.valueOf())
        ? `${this.#string('expires')} ${expires.toLocaleDateString(document.documentElement.lang || undefined, {
            day: 'numeric',
            month: 'long',
          })}.`
        : '';
  }

  /** @param {boolean} [keyboard] */
  #showLens(keyboard = false) {
    if (!this.#lensOn && !keyboard) return;
    const { lens, image, scene } = this.refs;
    const box = this.#box();
    const sceneBox = scene.getBoundingClientRect();
    const size = this.#lensSize();
    const zoom = Number(this.dataset.zoom) || 2.5;
    const px = (this.#point.x / 100) * box.width;
    const py = (this.#point.y / 100) * box.height;
    const layers = this.#lensLayers();

    if (layers !== this.#lensSrc) {
      this.#lensSrc = layers;
      lens.style.backgroundImage = layers;
    }
    lens.style.setProperty('--lens-size', `${size}px`);
    lens.style.backgroundSize = `${box.width * zoom}px ${box.height * zoom}px`;
    lens.style.backgroundPosition = `${size / 2 - px * zoom}px ${size / 2 - py * zoom}px`;
    lens.style.transform = `translate(${box.left - sceneBox.left + px - size / 2}px, ${box.top - sceneBox.top + py - size / 2}px)`;
    lens.toggleAttribute('data-visible', true);
  }

  /**
   * Background layers for the lens, top to bottom: the optional chalk-clue overlay, the
   * full-resolution artwork, then the already-loaded responsive image as a placeholder while
   * the full-resolution file arrives. All layers share one size and position, so they align.
   */
  #lensLayers() {
    const { image } = this.refs;
    const comp = this.#composition();
    const displayed = image.currentSrc || image.src;
    const full = (comp === 'm' ? this.dataset.lensSrcMobile : this.dataset.lensSrc) || '';
    const overlay = comp === 'd' ? this.dataset.lensOverlay || '' : '';
    const urls = [overlay, full, displayed]
      .filter(Boolean)
      .map((url) => new URL(url, window.location.href).href)
      .filter((url, index, list) => list.indexOf(url) === index);
    return urls.map((url) => `url("${url.replace(/"/g, '%22')}")`).join(', ');
  }

  /** Fetch the full-resolution artwork only once someone is about to use the lens. */
  #preloadLens() {
    const comp = this.#composition();
    const full = comp === 'm' ? this.dataset.lensSrcMobile : this.dataset.lensSrc;
    if (!full || this.#preloaded.has(full)) return;
    this.#preloaded.add(full);
    const img = new Image();
    img.decoding = 'async';
    img.src = full;
  }

  #hideLens() {
    this.refs.lens?.toggleAttribute('data-visible', false);
  }

  #lensSize() {
    const preferred = Number(this.dataset.lensSize) || 170;
    return Math.round(Math.min(preferred, this.#box().width * 0.42));
  }

  #syncLensToggle() {
    const { lensToggle, lensLabel } = this.refs;
    this.dataset.lens = this.#lensOn ? 'on' : 'off';
    if (lensToggle) lensToggle.setAttribute('aria-pressed', String(this.#lensOn));
    if (lensLabel) lensLabel.textContent = this.#string(this.#lensOn ? 'magnifier_on' : 'magnifier');
  }

  /** @param {Point} point */
  #rippleAt(point) {
    const dot = document.createElement('span');
    dot.className = 'f10__ripple-dot';
    dot.style.setProperty('--x', `${point.x}%`);
    dot.style.setProperty('--y', `${point.y}%`);
    this.refs.ripple.replaceChildren(dot);
  }

  /**
   * @param {string} message
   * @param {number} duration
   * @param {boolean} [html] message is pre-escaped markup
   */
  #toast(message, duration, html = false) {
    const { status } = this.refs;
    if (html) status.innerHTML = message;
    else status.textContent = message;
    clearTimeout(this.#toastTimer);
    this.#toastTimer = setTimeout(() => {
      status.textContent = '';
    }, duration);
  }

  #box() {
    return this.refs.image.getBoundingClientRect();
  }

  /** @param {string} key */
  #string(key) {
    const node = this.refs.strings?.content.querySelector(`[data-key="${key}"]`);
    return node?.textContent?.trim() || '';
  }

  /* ----------------------------------------------------------------- persistence & analytics */

  #save() {
    try {
      const data = {
        found: [...this.#found],
        finds: this.#finds,
        bonus: this.#bonusFound,
        startedAt: this.#startedAt,
        token: this.#token,
        claim: this.#claim,
        eaJoined: this.#eaJoined,
        eaSkipped: this.#eaSkipped,
        complete: this.dataset.state === 'complete',
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Storage can be unavailable (private mode); the game still works without it.
    }
  }

  /* ----------------------------------------------------------------- campaign window */

  #now() {
    return Date.now() + this.#clockOffset;
  }

  /**
   * Uses Shopify's server clock (the Date header of a tiny same-origin request) so a wrong device
   * clock can't extend the game. Liquid also closes the game on pages rendered after the deadline,
   * and the discount itself expires in Shopify at the same moment.
   */
  async #syncClock() {
    try {
      const sent = Date.now();
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        method: 'HEAD',
        cache: 'no-store',
        credentials: 'same-origin',
        signal: this.#abort?.signal,
      });
      const server = Date.parse(response.headers.get('Date') || '');
      if (!Number.isFinite(server)) return;
      const received = Date.now();
      this.#clockOffset = server + (received - sent) / 2 - received;
      this.#startCountdown();
    } catch {
      // Keep the device clock; the server-side checks still apply.
    }
  }

  #endTime() {
    const time = Date.parse(this.dataset.endsAt || '');
    return Number.isFinite(time) ? time : null;
  }

  /** Closes the game once the end time has passed. Returns true when it is over. */
  #checkDeadline() {
    if (this.dataset.state === 'ended') return true;
    const end = this.#endTime();
    if (end === null || this.#now() <= end) return false;
    this.#hideLens();
    this.dataset.state = 'ended';
    this.#track('game_closed', {}, 'game_closed');
    return true;
  }

  #startCountdown() {
    const { countdown } = this.refs;
    const tick = () => {
      const end = this.#endTime();
      if (this.#checkDeadline() || end === null) {
        clearInterval(this.#countdownTimer);
        if (countdown) countdown.hidden = true;
        return;
      }
      if (!countdown) return;
      const minutes = Math.max(1, Math.ceil((end - this.#now()) / 60000));
      const days = Math.floor(minutes / 1440);
      const hours = Math.floor((minutes % 1440) / 60);
      const mins = minutes % 60;
      const left = days > 0 ? `${days}d ${hours}h` : hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
      countdown.textContent = `${this.#string('ends_in')} ${left}`;
      countdown.hidden = false;
    };
    clearInterval(this.#countdownTimer);
    tick();
    this.#countdownTimer = setInterval(tick, 30000);
  }

  /* ----------------------------------------------------------------- early access registration */

  /**
   * After the early-access form's page reload. The section reads Shopify's form result on the
   * server (data-registration) and only then includes the code (data-reward-code).
   */
  #afterRegistration() {
    /** @type {{ id?: string, t?: number } | null} */
    let flag = null;
    try {
      flag = JSON.parse(window.sessionStorage.getItem(REGISTRATION_KEY) || 'null');
    } catch {}
    const fresh = Boolean(flag && flag.id === this.id && Date.now() - Number(flag.t) < REGISTRATION_TTL);
    const result = this.dataset.registration;
    if (!fresh || !result) return;

    // The early-access block reads the same flag once the page has loaded, so clear it afterwards.
    window.addEventListener(
      'load',
      () => {
        try {
          window.sessionStorage.removeItem(REGISTRATION_KEY);
        } catch {}
      },
      { once: true }
    );

    const code = (this.dataset.rewardCode || '').trim().toUpperCase();
    if (result === 'posted' && CODE_PATTERN.test(code) && this.#foundCount() >= this.#total() && this.#total() > 0) {
      this.#eaJoined = true;
      this.#claim = { code, expiresAt: this.dataset.endsAt || undefined };
      if (this.dataset.state !== 'ended') this.dataset.state = 'complete';
      this.#save();
      this.#track('early_access_signup', { source: 'find_10_things' }, 'early_access_signup');
      this.#track('reward_claimed', { code_type: 'shared' }, 'reward_claimed');
    } else if (result === 'failed' && this.refs.registerError) {
      // Completion is kept; the visitor can reopen the form and try again.
      this.refs.registerError.hidden = false;
    }

    const params = new URLSearchParams(window.location.search);
    if (params.has('customer_posted') || params.has('contact_posted')) {
      params.delete('customer_posted');
      params.delete('contact_posted');
      const query = params.toString();
      history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
    }
    this.#renderAll();
    requestAnimationFrame(() => this.refs.complete.scrollIntoView({ block: 'start' }));
  }

  #restore() {
    try {
      const data = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
      if (!data || typeof data !== 'object') return;
      const keys = new Set(
        [...this.#targets.filter((target) => !target.bonus), ...this.#mobileTargets].map((target) => target.key)
      );
      this.#found = new Set((Array.isArray(data.found) ? data.found : []).filter((key) => keys.has(key)));
      this.#finds = (Array.isArray(data.finds) ? data.finds : []).filter(
        (find) => find && keys.has(find.key) && this.#found.has(find.key)
      );
      this.#bonusFound = Boolean(data.bonus);
      this.#startedAt = Number(data.startedAt) || 0;
      this.#token = typeof data.token === 'string' ? data.token : null;
      if (data.claim && typeof data.claim.code === 'string' && CODE_PATTERN.test(data.claim.code) && !isExpired(data.claim.expiresAt)) {
        this.#claim = { code: data.claim.code, expiresAt: data.claim.expiresAt };
      }
      this.#eaJoined = Boolean(data.eaJoined);
      this.#eaSkipped = Boolean(data.eaSkipped);
      if (data.complete && this.#foundCount() >= this.#total() && this.#total() > 0 && this.dataset.state !== 'ended') {
        this.dataset.state = 'complete';
      }
    } catch {
      // Ignore unreadable saved progress.
    }
  }

  /**
   * Publishes a custom event to Shopify's customer events (Web Pixels) and, when present,
   * to an existing GTM dataLayer or gtag. No personal data is included.
   * @param {string} name
   * @param {Record<string, unknown>} [detail]
   * @param {string} [onceKey] fire at most once per game for this key
   */
  #track(name, detail = {}, onceKey) {
    if (onceKey) {
      if (this.#tracked.has(onceKey)) return;
      this.#tracked.add(onceKey);
    }
    const payload = { game: 'find_10_things', section_id: this.id, ...detail };
    try {
      /** @type {any} */ (window).Shopify?.analytics?.publish?.(name, payload);
    } catch {}
    try {
      const w = /** @type {any} */ (window);
      if (Array.isArray(w.dataLayer)) w.dataLayer.push({ event: name, ...payload });
      else if (typeof w.gtag === 'function') w.gtag('event', name, payload);
    } catch {}
    this.dispatchEvent(new CustomEvent(`odessey-find:${name}`, { bubbles: true, detail: payload }));
  }
}

class ClaimError extends Error {
  /** @param {string} key */
  constructor(key) {
    super(key);
    this.key = key;
  }
}

/** @param {any} target @returns {target is Target} */
function isTarget(target) {
  const spot = (/** @type {any} */ s) => s && Number.isFinite(s.x) && Number.isFinite(s.y) && Number.isFinite(s.r);
  return Boolean(target && typeof target.key === 'string' && target.key && spot(target.d) && spot(target.m));
}

/** @param {any} target */
function isMobileTarget(target) {
  const unit = (/** @type {any} */ value) => Number.isFinite(value) && value >= 0 && value <= 1;
  return Boolean(
    target && typeof target.key === 'string' && target.key && unit(target.x) && unit(target.y) && unit(target.w) && unit(target.h)
  );
}

/**
 * Mobile blocks are normalized (0 to 1); the game works in percent like the desktop map.
 * @param {any} target
 * @returns {Active}
 */
function toActive(target) {
  return {
    key: target.key,
    bonus: false,
    label: String(target.label || ''),
    message: String(target.message || ''),
    hint: String(target.hint || ''),
    x: target.x * 100,
    y: target.y * 100,
    w: target.w * 100,
    h: target.h * 100,
  };
}

/** @param {Active} target */
function normalizedSpot(target) {
  const n = (/** @type {number} */ value) => Math.round(value * 10) / 1000;
  if (target.w !== undefined && target.h !== undefined) {
    return { x: n(target.x), y: n(target.y), width: n(target.w), height: n(target.h) };
  }
  return { x: n(target.x), y: n(target.y), radius: n(target.r ?? 0) };
}

/** @param {Active} target */
function describeSpot(target) {
  const spot = normalizedSpot(target);
  return 'width' in spot
    ? `x ${spot.x} y ${spot.y} · w ${spot.width} h ${spot.height}`
    : `x ${spot.x} y ${spot.y} · r ${spot.radius}`;
}

/**
 * Target outlines are for checking the map before launch: only on an unpublished (preview) theme
 * with ?debugTargets=1, or in the theme editor with the section's debug setting on. Never on the
 * live theme for visitors.
 * @param {boolean} editorSetting
 */
function debugAllowed(editorSetting) {
  const shopify = /** @type {any} */ (window).Shopify;
  const editor = Boolean(shopify?.designMode);
  if (editor && editorSetting) return true;
  let requested = false;
  try {
    requested = new URLSearchParams(window.location.search).get(DEBUG_PARAM) === '1';
  } catch {}
  const role = shopify?.theme?.role;
  return requested && (editor || (typeof role === 'string' && role !== 'main'));
}

/** @param {number} value @param {number} min @param {number} max */
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/** @param {number} value */
function round2(value) {
  return Math.round(value * 100) / 100;
}

/** @param {string | undefined} iso */
function isExpired(iso) {
  if (!iso) return false;
  const time = Date.parse(iso);
  return Number.isFinite(time) && time < Date.now();
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** @param {string} value */
function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
}

if (!customElements.get('odessey-find-game')) {
  customElements.define('odessey-find-game', OdesseyFindGame);
}

